import { useCallback } from 'react';
import {
	receptionAdminControllerStatusV1,
	receptionAdminControllerEnterV1
} from '@api/generated/admin-reception/admin-reception';
import {
	receptionModeControllerExitV1,
	receptionModeControllerChangeManagerV1
} from '@api/generated/reception-mode/reception-mode';
import type { ReceptionStateResponseDto } from '@api/generated/model';
import { useAuthStore } from '@store/useAuthStore';

// 리셉션 모드 진입/이탈/담당자 변경/상태조회 API 훅
// enter(관리자 API)/changeManager(리셉션모드 API)/syncStatus: 응답으로 스토어 setReception, exit: clearReception
export const useReceptionMode = () => {
	const setReception = useAuthStore(s => s.setReception);
	const clearReception = useAuthStore(s => s.clearReception);

	// 서버(Redis)의 현재 리셉션 상태를 조회해 로컬 스토어에 동기화 (비밀번호 불필요)
	// 새로고침/재접속 시 8시간 TTL 내라면 재진입 없이 상태 복원
	const syncStatus = useCallback(async (): Promise<ReceptionStateResponseDto> => {
		const data = await receptionAdminControllerStatusV1();
		setReception(data);
		return data;
	}, [setReception]);

	// 리셉션 모드 진입 (관리자 비밀번호 + 초기 담당자명)
	const enter = useCallback(
		async (password: string, managerName: string): Promise<ReceptionStateResponseDto> => {
			const data = await receptionAdminControllerEnterV1({ password, managerName });
			setReception(data);
			return data;
		},
		[setReception]
	);

	// 리셉션 모드 이탈 (관리자 비밀번호)
	const exit = useCallback(
		async (password: string): Promise<void> => {
			await receptionModeControllerExitV1({ password });
			clearReception();
		},
		[clearReception]
	);

	// 담당자 변경 (담당자명 + 관리자 비밀번호)
	const changeManager = useCallback(
		async (managerName: string, password: string): Promise<ReceptionStateResponseDto> => {
			const data = await receptionModeControllerChangeManagerV1({ managerName, password });
			setReception(data);
			return data;
		},
		[setReception]
	);

	return { syncStatus, enter, exit, changeManager };
};
